// Pages légales — Mentions légales, CGU, RGPD, Paramètres cookies
// Un seul composant qui dispatche selon le `kind`. Contenu rédigé conforme
// aux exigences françaises (LCEN 2004-575, RGPD 2018, recommandations CNIL).

import { useEffect, useState } from"react";
import { Scale, FileText, Shield, Cookie, Check, X } from"lucide-react";

// Métadonnées éditeur
const PUBLISHER = {
  name:"KEYTO",
  legalForm:"Société au capital de 1 000 €",
  address:"34 av. des Champs-Élysées, 75008 Paris",
  rcs:"951 107 929 R.C.S. Paris",
  siret:"951 107 929 00014",
  vat:"FR26951107929",
  director:"Kevin Tordjman",
  phone:"06 25 70 43 65",
  email:"k.tordjman@keyto-expertise.com",
  website:"https://keyto.fr",
};

const HOSTING = {
  provider:"IONOS SARL",
  address:"7 place de la Gare, 57200 Sarreguemines",
  phone:"0805 542 180",
};

// ─────────────────────────────────────────────────────────────
// Contenu : Mentions légales (LCEN art. 6-III)
// ─────────────────────────────────────────────────────────────
function MentionsLegales() {
  return (
    <article className="space-y-6">
      <Section title="1. Éditeur du site">
        <KV k="Dénomination sociale" v={PUBLISHER.name} />
        <KV k="Capital social" v={PUBLISHER.legalForm} />
        <KV k="Siège social" v={PUBLISHER.address} />
        <KV k="RCS / SIREN" v={PUBLISHER.rcs} />
        <KV k="SIRET" v={PUBLISHER.siret} />
        <KV k="N° TVA intracommunautaire" v={PUBLISHER.vat} />
        <KV k="Directeur de la publication" v={PUBLISHER.director} />
        <KV k="Téléphone" v={PUBLISHER.phone} />
        <KV k="Courriel" v={PUBLISHER.email} />
      </Section>

      <Section title="2. Hébergeur">
        <KV k="Hébergeur" v={HOSTING.provider} />
        <KV k="Adresse" v={HOSTING.address} />
        <KV k="Téléphone" v={HOSTING.phone} />
      </Section>

      <Section title="3. Propriété intellectuelle">
        <p>
          L&apos;ensemble du site PCG interactif by KEYTO — sa structure, ses textes
          pédagogiques, ses fiches comptables, ses schémas, son code source, ses
          logos et identité visuelle — est protégé par le droit d&apos;auteur (Code de
          la propriété intellectuelle, art. L.111-1 et suivants).
        </p>
        <p>
          Toute reproduction, représentation, modification, traduction, exploitation
          commerciale ou rediffusion, totale ou partielle, par quelque procédé que
          ce soit, est strictement interdite sans l&apos;autorisation écrite préalable
          de l&apos;éditeur. La violation de ces droits expose son auteur à des
          poursuites au titre de la contrefaçon (art. L.335-2 et suivants du CPI).
        </p>
        <p>
          Le Plan Comptable Général reste un référentiel public régi par le règlement
          ANC n°2014-03 et ses modifications successives. Les libellés officiels des
          comptes sont reproduits à des fins strictement pédagogiques.
        </p>
      </Section>

      <Section title="4. Sources et exactitude des informations">
        <p>
          Les contenus comptables présentés s&apos;appuient sur le Plan Comptable
          Général en vigueur (règlement ANC n°2014-03 modifié), sur les commentaires
          de l&apos;Autorité des Normes Comptables, et sur la doctrine professionnelle
          de l&apos;Ordre des Experts-Comptables.
        </p>
        <p>
          Malgré tout le soin apporté à la rédaction, les informations fournies ont
          une finalité <strong>pédagogique</strong> et ne sauraient se substituer à
          un conseil professionnel personnalisé. L&apos;éditeur ne saurait être tenu
          responsable des conséquences d&apos;une utilisation des contenus pour la
          tenue d&apos;une comptabilité réelle.
        </p>
      </Section>

      <Section title="5. Liens hypertextes">
        <p>
          Le site peut contenir des liens vers des sites tiers (administrations,
          référentiels comptables, sources doctrinales). L&apos;éditeur n&apos;exerce
          aucun contrôle sur ces sites et décline toute responsabilité quant à leur
          contenu.
        </p>
      </Section>

      <Section title="6. Droit applicable">
        <p>
          Les présentes mentions sont régies par le droit français. Tout litige
          relatif à l&apos;utilisation du site relève de la compétence exclusive des
          tribunaux français, sous réserve des règles de compétence territoriale
          d&apos;ordre public.
        </p>
      </Section>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// Contenu : Conditions Générales d'Utilisation
// ─────────────────────────────────────────────────────────────
function CGU() {
  return (
    <article className="space-y-6">
      <Section title="Article 1 — Objet">
        <p>
          Les présentes Conditions Générales d&apos;Utilisation (« CGU ») ont pour
          objet de définir les modalités d&apos;accès et d&apos;utilisation du site
          <em> PCG interactif by KEYTO </em>(ci-après « le Site »), édité par
          {" "}{PUBLISHER.name}.
        </p>
        <p>
          L&apos;accès et l&apos;usage du Site impliquent l&apos;acceptation pleine
          et entière des présentes CGU. L&apos;utilisateur qui ne les accepte pas
          doit cesser immédiatement toute utilisation du Site.
        </p>
      </Section>

      <Section title="Article 2 — Accès au service">
        <p>
          Le Site est accessible gratuitement, 24 h / 24 et 7 j / 7, à toute personne
          disposant d&apos;une connexion Internet. Aucune création de compte
          n&apos;est requise pour la consultation. Les coûts d&apos;accès et
          d&apos;usage du réseau (matériel, abonnement, communications) restent à la
          charge de l&apos;utilisateur.
        </p>
        <p>
          L&apos;éditeur se réserve le droit, sans préavis ni indemnité, d&apos;en
          suspendre l&apos;accès pour effectuer une maintenance, une mise à jour ou
          en cas de force majeure.
        </p>
      </Section>

      <Section title="Article 3 — Nature du service">
        <p>
          Le Site propose un parcours pédagogique interactif autour du Plan Comptable
          Général : fiches d&apos;explication, exemples chiffrés, comparateur de
          comptes, méthodologie de saisie, glossaire, questions fréquentes et un
          assistant d&apos;aide à la compréhension.
        </p>
        <p>
          Ces contenus ont une vocation strictement <strong>pédagogique</strong> et
          ne constituent ni un conseil comptable, fiscal ou juridique personnalisé,
          ni une prestation d&apos;expertise comptable au sens de l&apos;ordonnance
          n°45-2138 du 19 septembre 1945.
        </p>
      </Section>

      <Section title="Article 4 — Comportements interdits">
        <p>L&apos;utilisateur s&apos;engage à ne pas :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>extraire, copier ou rediffuser tout ou partie des contenus à des fins commerciales ;</li>
          <li>perturber le fonctionnement du Site (intrusion, scraping massif, déni de service) ;</li>
          <li>contourner les mesures techniques de protection ou les limites d&apos;usage de l&apos;assistant IA ;</li>
          <li>publier, via les zones d&apos;interaction, des contenus illicites, diffamatoires ou portant atteinte aux droits de tiers.</li>
        </ul>
      </Section>

      <Section title="Article 5 — Responsabilité">
        <p>
          L&apos;éditeur met en œuvre tous les moyens raisonnables pour assurer
          l&apos;exactitude et la disponibilité des informations diffusées. Il ne
          peut toutefois garantir l&apos;absence d&apos;erreur, de lacune ou de
          mise à jour, ni la pertinence des informations dans tout cas particulier.
        </p>
        <p>
          L&apos;utilisateur reste seul responsable de l&apos;usage qu&apos;il fait
          des contenus, en particulier dans le cadre de la tenue ou de la révision
          d&apos;une comptabilité. Pour toute opération comptable réelle,
          l&apos;utilisateur est invité à consulter un expert-comptable.
        </p>
      </Section>

      <Section title="Article 6 — Données personnelles">
        <p>
          Les modalités de traitement des données personnelles sont décrites dans
          la <strong>politique RGPD</strong> du Site, accessible depuis le pied de page.
        </p>
      </Section>

      <Section title="Article 7 — Modification des CGU">
        <p>
          L&apos;éditeur se réserve le droit de modifier à tout moment les présentes
          CGU. La version applicable est celle en ligne au moment de la consultation.
          Il appartient à l&apos;utilisateur de s&apos;y reporter régulièrement.
        </p>
      </Section>

      <Section title="Article 8 — Droit applicable et juridiction compétente">
        <p>
          Les présentes CGU sont soumises au droit français. À défaut de résolution
          amiable, tout litige relève de la compétence exclusive des tribunaux
          français.
        </p>
      </Section>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// Contenu : Politique RGPD
// ─────────────────────────────────────────────────────────────
function RGPD() {
  return (
    <article className="space-y-6">
      <Section title="1. Responsable de traitement">
        <p>
          Le responsable de traitement des données collectées sur le Site est
          {" "}<strong>{PUBLISHER.name}</strong>, {PUBLISHER.address}.
        </p>
        <p>
          Pour toute demande relative à vos données personnelles, vous pouvez nous
          écrire à : <a href={`mailto:${PUBLISHER.email}`} className="text-[#2D6BE0] underline">{PUBLISHER.email}</a>.
        </p>
      </Section>

      <Section title="2. Données collectées et finalités">
        <p>
          Le Site collecte un nombre limité de données, dans le strict respect du
          principe de minimisation (RGPD art. 5-1-c) :
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Données de navigation anonymisées</strong> (pages vues, durée, navigateur, pays) — finalité : mesure d&apos;audience.</li>
          <li><strong>Préférences utilisateur</strong> (favoris, dernière vue, statistiques de quiz) — stockées localement sur votre appareil ; aucune transmission au serveur.</li>
          <li><strong>Échanges avec l&apos;assistant IA</strong> — les questions posées sont transmises à un fournisseur de modèle de langage (Anthropic, via l&apos;intégration Emergent), pour le seul besoin de générer la réponse. Aucun historique n&apos;est conservé côté serveur.</li>
        </ul>
        <p>
          Le Site <strong>ne collecte pas de données identifiantes</strong> : pas
          d&apos;inscription, pas de création de compte, pas de mot de passe.
        </p>
      </Section>

      <Section title="3. Base légale">
        <p>
          Les traitements reposent sur les bases légales suivantes (RGPD art. 6) :
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Intérêt légitime</strong> pour la mesure d&apos;audience anonymisée et la sécurité du Site ;</li>
          <li><strong>Consentement</strong> pour les cookies non essentiels (voir Paramètres cookies) ;</li>
          <li><strong>Exécution du service</strong> pour la transmission de votre question à l&apos;assistant IA.</li>
        </ul>
      </Section>

      <Section title="4. Durée de conservation">
        <ul className="list-disc pl-6 space-y-1">
          <li>Statistiques d&apos;audience anonymisées : <strong>13 mois</strong> maximum (recommandation CNIL).</li>
          <li>Préférences locales : tant que vous ne videz pas le stockage de votre navigateur.</li>
          <li>Conversations IA : non conservées au-delà de la session.</li>
        </ul>
      </Section>

      <Section title="5. Destinataires et sous-traitants">
        <p>
          Vos données peuvent être traitées par les sous-traitants suivants, agissant
          sur les seules instructions de l&apos;éditeur, dans le cadre d&apos;accords
          de confidentialité conformes à l&apos;article 28 du RGPD :
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>IONOS SARL</strong> — hébergement du site (serveurs en France).</li>
          <li><strong>Anthropic</strong> — fournisseur du modèle de langage Claude utilisé par l&apos;assistant IA.</li>
          <li><strong>PostHog</strong> — outil de mesure d&apos;audience anonymisée.</li>
        </ul>
        <p>
          Aucune donnée n&apos;est cédée, vendue ou louée à des tiers à des fins
          commerciales.
        </p>
      </Section>

      <Section title="6. Transferts hors Union européenne">
        <p>
          Certains sous-traitants sont susceptibles de traiter des données hors UE
          (notamment États-Unis). Ces transferts sont encadrés par les{" "}
          <strong>clauses contractuelles types</strong> de la Commission européenne
          (décision 2021/914) et par les engagements de chaque sous-traitant.
        </p>
      </Section>

      <Section title="7. Vos droits">
        <p>Conformément au RGPD (art. 15 à 22) et à la loi Informatique et Libertés, vous disposez :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>d&apos;un droit d&apos;<strong>accès</strong> à vos données ;</li>
          <li>d&apos;un droit de <strong>rectification</strong> ;</li>
          <li>d&apos;un droit à l&apos;<strong>effacement</strong> ;</li>
          <li>d&apos;un droit à la <strong>limitation</strong> du traitement ;</li>
          <li>d&apos;un droit d&apos;<strong>opposition</strong> ;</li>
          <li>d&apos;un droit à la <strong>portabilité</strong>.</li>
        </ul>
        <p>
          Vous pouvez exercer ces droits en écrivant à
          {" "}<a href={`mailto:${PUBLISHER.email}`} className="text-[#2D6BE0] underline">{PUBLISHER.email}</a>.
        </p>
        <p>
          Si vous estimez que le traitement de vos données est non conforme, vous
          pouvez introduire une réclamation auprès de la <strong>CNIL</strong> :
          {" "}<a href="https://www.cnil.fr" target="_blank" rel="noreferrer" className="text-[#2D6BE0] underline">www.cnil.fr</a>.
        </p>
      </Section>

      <Section title="8. Sécurité">
        <p>
          L&apos;éditeur met en œuvre des mesures techniques et organisationnelles
          adaptées (chiffrement TLS, contrôle d&apos;accès, journalisation) pour
          protéger les données contre tout accès non autorisé, perte ou altération.
        </p>
      </Section>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// Contenu : Paramètres cookies (avec gestion fonctionnelle)
// ─────────────────────────────────────────────────────────────
const CONSENT_KEY ="keyto.cookieConsent.v1";

function ParametresCookies() {
  const [consent, setConsent] = useState({ analytics: false });
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setConsent({ analytics: !!parsed.analytics });
        setSavedAt(parsed.savedAt || null);
      }
    } catch (e) {
      console.warn("Cookie consent: corrupt localStorage, ignoring previous value", e);
    }
  }, []);

  const save = (next) => {
    const payload = { ...next, savedAt: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    setConsent({ analytics: next.analytics });
    setSavedAt(payload.savedAt);
    // Optionnel : on peut aussi désactiver PostHog côté client si refus
    if (window.posthog && typeof window.posthog.opt_out_capturing ==="function") {
      if (!next.analytics) window.posthog.opt_out_capturing();
      else window.posthog.opt_in_capturing();
    }
  };

  return (
    <article className="space-y-6">
      <Section title="1. Qu'est-ce qu'un cookie ?">
        <p>
          Un cookie est un petit fichier texte déposé sur votre appareil lors de la
          consultation d&apos;un site. Il permet de mémoriser des préférences ou de
          mesurer l&apos;audience de manière anonyme. Conformément à l&apos;article
          82 de la loi Informatique et Libertés et aux recommandations de la CNIL,
          vous pouvez accepter, refuser ou paramétrer leur utilisation à tout
          moment.
        </p>
      </Section>

      <Section title="2. Cookies utilisés sur ce site">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-keyto rounded-lg overflow-hidden">
            <thead className="bg-slate-100 text-[#15294F]">
              <tr>
                <th className="text-left px-3 py-2 border-b border-keyto">Catégorie</th>
                <th className="text-left px-3 py-2 border-b border-keyto">Finalité</th>
                <th className="text-left px-3 py-2 border-b border-keyto">Durée</th>
                <th className="text-left px-3 py-2 border-b border-keyto">Statut</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="px-3 py-2 border-b border-keyto font-semibold">Strictement nécessaires</td>
                <td className="px-3 py-2 border-b border-keyto">Fonctionnement du site (préférences, favoris, navigation)</td>
                <td className="px-3 py-2 border-b border-keyto">Session / 12 mois</td>
                <td className="px-3 py-2 border-b border-keyto">
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                    <Check size={12} /> Toujours actifs
                  </span>
                </td>
              </tr>
              <tr className="bg-white">
                <td className="px-3 py-2 font-semibold">Mesure d&apos;audience anonyme</td>
                <td className="px-3 py-2">Statistiques de fréquentation (PostHog) — IP anonymisée</td>
                <td className="px-3 py-2">13 mois max.</td>
                <td className="px-3 py-2">
                  {consent.analytics ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                      <Check size={12} /> Activée
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-700">
                      <X size={12} /> Désactivée
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="3. Gérer mes préférences">
        <div className="space-y-3">
          <Toggle
            label="Cookies strictement nécessaires"
            desc="Indispensables au fonctionnement du site. Ne peuvent pas être désactivés."
            checked
            disabled
            onChange={() => {}}
            testid="cookie-toggle-essential"
          />
          <Toggle
            label="Mesure d'audience anonyme"
            desc="Nous aide à comprendre quelles fiches sont les plus consultées. Aucune donnée identifiante."
            checked={consent.analytics}
            disabled={false}
            onChange={(v) => save({ analytics: v })}
            testid="cookie-toggle-analytics"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            data-testid="cookie-accept-all"
            onClick={() => save({ analytics: true })}
            className="px-4 py-2 bg-[#15294F] text-white text-xs font-bold rounded-md hover:bg-[#1F3B6B] transition"
          >
            Tout accepter
          </button>
          <button
            data-testid="cookie-reject-all"
            onClick={() => save({ analytics: false })}
            className="px-4 py-2 bg-white border border-keyto text-[#15294F] text-xs font-bold rounded-md hover:bg-slate-50 transition"
          >
            Tout refuser
          </button>
        </div>
        {savedAt && (
          <p className="text-[11px] text-slate-500 italic">
            Préférences enregistrées le {new Date(savedAt).toLocaleString("fr-FR")}.
          </p>
        )}
      </Section>

      <Section title="4. Ressources">
        <p>
          Pour en savoir plus sur les cookies et vos droits, consultez le site de la{" "}
          <a href="https://www.cnil.fr/fr/cookies-et-traceurs" target="_blank" rel="noreferrer" className="text-[#2D6BE0] underline">
            CNIL — Cookies et traceurs
          </a>.
        </p>
      </Section>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// UI helpers
// ─────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-heading text-lg md:text-xl font-extrabold text-[#15294F] border-b border-keyto pb-2">
        {title}
      </h2>
      <div className="space-y-3 text-sm text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function KV({ k, v }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-1 md:gap-3 py-1 border-b border-slate-100 last:border-0">
      <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{k}</span>
      <span className="text-sm text-slate-800">{v}</span>
    </div>
  );
}

function Toggle({ label, desc, checked, disabled, onChange, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-4 border border-keyto rounded-lg ${
        disabled ?"bg-slate-50 cursor-not-allowed" :"bg-white hover:border-[#2D6BE0] cursor-pointer transition"
      }`}
    >
      <button
        type="button"
        data-testid={testid}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        className={`relative shrink-0 mt-0.5 w-10 h-6 rounded-full transition-colors ${
          checked ?"bg-[#2D6BE0]" :"bg-slate-300"
        } ${disabled ?"opacity-60" :""}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ?"translate-x-4" :""
          }`}
        />
      </button>
      <div className="flex-1">
        <p className="font-bold text-sm text-[#15294F]">{label}</p>
        <p className="text-[12px] text-slate-600 mt-0.5">{desc}</p>
      </div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// Composant principal — dispatch sur kind
// ─────────────────────────────────────────────────────────────
const TITLES = {
  mentions: { icon: Scale, label:"Mentions légales", subtitle:"Informations légales sur l'éditeur, l'hébergeur et les conditions de publication du site." },
  cgu: { icon: FileText, label:"Conditions Générales d'Utilisation", subtitle:"Règles applicables à l'accès et à l'usage du Site PCG interactif by KEYTO." },
  rgpd: { icon: Shield, label:"Politique de confidentialité — RGPD", subtitle:"Engagements en matière de protection des données personnelles." },
  cookies: { icon: Cookie, label:"Paramètres cookies", subtitle:"Gérez vos préférences en matière de traceurs déposés sur votre navigateur." },
};

export default function Legal({ kind }) {
  const meta = TITLES[kind] || TITLES.mentions;
  const Icon = meta.icon;

  return (
    <div data-testid={`legal-${kind}-view`} className="max-w-4xl mx-auto space-y-8">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#15294F] via-[#1E3A6F] to-[#15294F] text-white rounded-2xl shadow-keyto p-7 md:p-9 flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
          <Icon size={22} className="text-[#5AA0FF]" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#5AA0FF] mb-1">
            Informations légales
          </p>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold leading-tight">
            {meta.label}
          </h1>
          <p className="mt-2 text-sm text-white/75 max-w-2xl leading-relaxed">{meta.subtitle}</p>
          <p className="mt-3 text-[11px] text-white/50">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year:"numeric", month:"long", day:"numeric" })}
          </p>
        </div>
      </div>

      {/* Contenu */}
      <div className="bg-white border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
        {kind ==="mentions" && <MentionsLegales />}
        {kind ==="cgu" && <CGU />}
        {kind ==="rgpd" && <RGPD />}
        {kind ==="cookies" && <ParametresCookies />}
      </div>
    </div>
  );
}
